import { useApp } from '../state/store';
import { back, navigate } from '../lib/router';
import { C, R, font } from '../theme/tokens';
import { roleOf } from '../state/selectors';
import { dayLabel, ymdIn } from '../lib/dates';
import { Avatar, BackPill, Btn, Card, CardTitle, DarkCard, EmptyState, Eyebrow, Photo, StageTag, Tag, safeTop } from '../components/ui';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronIcon, ImageIcon, LinkIcon, LockIcon, PaletteIcon, PencilIcon, ShareIcon, UnitIcon } from '../components/icons';
import { UnitBadge } from '../components/affiliations';
import type { EditSection } from '../state/reducer';
import { useAuth } from '../auth/AuthGate';
import { apiActive } from '../services/api';
import { PHOTO_ACCEPT, PhotoError, preparePhoto } from '../lib/image';
import { getSession, isApprover } from '../services/auth';
import { copyText, nativeShare } from '../services/share';
import { displayLink } from '../lib/unitReferral';
import type { Ambassador } from '../domain/types';
import { orderedSeries, seriesLabel } from '../domain/series';
import { isDemo } from '../lib/clock';

const PencilGlyph = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);

/** Outline camera, sized like the header icons on the public landing. */
function CameraGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13.5" r="3.5" />
    </svg>
  );
}

/** Me (own, editable) and other ambassadors' profiles share one layout. */
export function Profile({ id }: { id?: string }) {
  const { state: { data }, act } = useApp();
  const { signOut } = useAuth();
  // Cover photo change: pick → preview → Save (server) or Cancel. Nothing is kept locally after Save.
  const coverInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<{ blob: Blob; url: string } | null>(null);
  const [savingCover, setSavingCover] = useState(false);
  useEffect(() => () => { if (draft) URL.revokeObjectURL(draft.url); }, [draft]);
  const pid = id ?? data.me;
  const p = data.ambassadors[pid];
  if (!p) return <EmptyState title="Ambassador not found." action={<Btn variant="primary" onClick={() => navigate({ name: 'discover' })}>Back to Discover</Btn>} />;

  const isMe = pid === data.me;
  const pr = data.privacy;
  const following = Boolean(data.followedPeople[pid]);
  const acts = Object.values(data.activities).filter((a) => a.team.some((m) => m.ambassadorId === pid)).sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  const shares = data.shareRecords.length;
  const updatesPosted = data.updates.filter((u) => u.authorId === pid).length;
  const showCity = !isMe || pr.showCity;
  const showSocials = !isMe || pr.showSocials;

  const shareProfile = async () => {
    // Sharing never widens visibility: a private profile is not shared, and a hidden city stays out of the text.
    if (!pr.publicProfile) return act.toast('Your profile is visible to your teams only. Turn on Public profile in Privacy to share it.');
    const title = !pr.showCity && p.city && p.title.startsWith(`${p.city} `) ? p.title.slice(p.city.length + 1) : p.title;
    const text = `Meet ${p.name}, ${title} on UGAIN.`;
    if (navigator.share) {
      try { await navigator.share({ title: 'UGAIN', text }); } catch { /* cancelled */ }
    } else act.toast('On a phone this opens the share sheet');
  };

  const pickCover = async (file: File | undefined) => {
    if (coverInput.current) coverInput.current.value = '';
    if (!file) return;
    try {
      const blob = await preparePhoto(file, 'cover');
      setDraft({ blob, url: URL.createObjectURL(blob) });
    } catch (err) {
      act.toast(err instanceof PhotoError ? err.message : 'That photo couldn’t be read. Try another.');
    }
  };
  const saveCover = async () => {
    if (!draft || savingCover) return;
    setSavingCover(true);
    const ok = await act.savePhoto('cover', draft.blob); // on failure the preview stays so Save can be retried
    setSavingCover(false);
    if (ok) setDraft(null);
  };
  const changeCover = () => {
    if (!apiActive) return act.toast('Sign in with Google to change your cover photo.');
    coverInput.current?.click();
  };
  const coverSrc = draft?.url ?? p.coverUrl;

  return (
    <div className="fade">
      <div style={{ position: 'relative', height: 380, background: C.night }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          {coverSrc ? <img src={coverSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : <Photo id={p.cover} eager fallback="" alt="" />}
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(11,11,15,.5) 0%,rgba(11,11,15,0) 30%,rgba(11,11,15,.94) 100%)' }} />
        <div style={{ position: 'absolute', top: safeTop(10), left: 16, right: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!isMe ? <BackPill onPhoto onClick={back} /> : <span />}
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!isMe && <Tag label="AMBASSADOR" bg={C.white} fg={C.ink} radius={R.pill} style={{ padding: '9px 12px' }} />}
            {isMe && !draft && (
              <span className="hs-trigger-wrap">
                <button type="button" className="hs-trigger me-cover press" aria-label="Change cover photo" onClick={changeCover}>
                  <CameraGlyph />
                </button>
                <span className="hs-tip" role="tooltip">Change cover photo</span>
              </span>
            )}
            {isMe && draft && (
              <>
                <Btn size="md" variant="white" disabled={savingCover} onClick={() => setDraft(null)}>Cancel</Btn>
                <Btn size="md" variant="primary" disabled={savingCover} aria-busy={savingCover} onClick={() => void saveCover()}>{savingCover ? 'Saving…' : 'Save'}</Btn>
              </>
            )}
            {isMe && <input ref={coverInput} type="file" accept={PHOTO_ACCEPT} hidden onChange={(e) => void pickCover(e.target.files?.[0])} />}
          </span>
        </div>
        <div style={{ position: 'absolute', left: 20, right: 20, bottom: 20, color: C.white, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Avatar person={p} size={76} ring={C.rim} />
          <h1 style={{ margin: 0, font: font(800, 46, 0.9), letterSpacing: '-.05em' }}>{p.name}</h1>
          <span style={{ font: font(600, 11, 1.3, 'mono'), color: C.metal, letterSpacing: '.04em' }}>{`${p.title} · ${showCity ? p.city : 'City hidden'}`.toUpperCase()}</span>
        </div>
      </div>

      <div style={{ padding: '14px 12px 0', display: 'flex', gap: 8 }}>
        {isMe ? (
          <>
            <Btn variant="primary" size="lg" style={{ flex: 1 }} onClick={shareProfile}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><ShareIcon size={18} />Share profile</span></Btn>
            <Btn size="lg" style={{ flex: 1 }} onClick={() => act.openSheet({ kind: 'profile', section: 'intro' })}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><PencilIcon size={18} />Edit</span></Btn>
          </>
        ) : (
          // Direct messages are not built yet, so there is no Message action here.
          <Btn variant={following ? 'ink' : 'primary'} size="lg" style={{ flex: 1 }} aria-pressed={following} onClick={() => act.toggleFollowPerson(pid)}>{following ? 'Following ✓' : 'Follow'}</Btn>
        )}
      </div>

      <DarkCard style={{ padding: 20, gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <Eyebrow color={C.rim} style={{ fontSize: '0.5625rem' }}>{isMe ? 'My story' : `${p.name.split(' ')[0]}'s story`}</Eyebrow>
          {isMe && (
            <button type="button" className="icon-dark press" aria-label="Edit introduction" title="Edit introduction" onClick={() => act.openSheet({ kind: 'profile', section: 'intro' })}>
              <PencilGlyph />
            </button>
          )}
        </div>
        {p.bio || isMe
          ? <p style={{ margin: 0, font: font(600, 21, 1.3), letterSpacing: '-.02em' }}>{p.bio}</p>
          : <p style={{ margin: 0, font: font(400, 14, 1.45), color: C.mutedDark }}>{`${p.name.split(' ')[0]} shares their story with their activity teams.`}</p>}
      </DarkCard>

      {isMe && (
        <div style={{ margin: '14px 12px 0', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 }}>
          {[{ v: acts.length, l: 'Activities' }, { v: updatesPosted, l: 'Updates posted' }, { v: shares, l: 'Shares recorded' }].map((x) => (
            <div key={x.l} style={{ background: C.white, border: `1px solid ${C.subtle}`, borderRadius: R.panel, padding: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ font: font(800, 28, 1), letterSpacing: '-.04em' }}>{x.v}</span>
              <span style={{ font: font(500, 11, 1.2), color: C.grey }}>{x.l}</span>
            </div>
          ))}
        </div>
      )}

      {p.achievements.length > 0 && (
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Eyebrow>Selected achievements</Eyebrow>
          {p.achievements.map((a) => (
            <span key={a} style={{ display: 'flex', gap: 10, alignItems: 'baseline', font: font(600, 15, 1.35) }}><span aria-hidden="true" style={{ color: C.violet }}>✦</span>{a}</span>
          ))}
        </Card>
      )}

      <AffiliationsRow person={p} isMe={isMe} />

      {!isMe && <UnitReferralCard person={p} />}

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Eyebrow>Interests</Eyebrow>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{p.interests.map((i) => <span key={i} style={{ height: 34, padding: '0 14px', borderRadius: R.pill, background: C.soft, color: C.violet, font: font(600, 13), display: 'flex', alignItems: 'center' }}>{i}</span>)}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Eyebrow>Skills</Eyebrow>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{p.skills.map((i) => <span key={i} style={{ height: 34, padding: '0 14px', borderRadius: R.pill, border: `1px solid ${C.border}`, font: font(600, 13), display: 'flex', alignItems: 'center' }}>{i}</span>)}</div>
        </div>
      </Card>

      <Card style={{ padding: '18px 18px 8px' }}>
        <CardTitle>Activities</CardTitle>
        {acts.map((a) => (
          <button key={a.id} type="button" onClick={() => navigate({ name: 'activity', id: a.id })}
            style={{ width: '100%', display: 'grid', gridTemplateColumns: '52px 1fr auto', gap: 12, alignItems: 'center', padding: '12px 0', border: 0, borderBottom: `1px solid ${C.divider}`, background: 'transparent', textAlign: 'left' }}>
            <span style={{ width: 52, height: 52, borderRadius: R.thumb, overflow: 'hidden', background: C.divider }}><Photo id={a.photo} pad={4} fallback={a.series.slice(0, 1)} sizes="52px" style={{ padding: a.photo ? undefined : 8 }} /></span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ font: font(600, 14, 1.2), color: C.ink }}>{a.name}</span>
              <span style={{ font: font(500, 11), color: C.violet }}>{roleOf(data, a.id, pid)}</span>
              <span style={{ font: font(400, 11), color: C.grey }}>{`${dayLabel(ymdIn(a.startsAt, a.timeZone))} · ${a.city}`}</span>
            </span>
            <StageTag stage={a.stage} />
          </button>
        ))}
        {!acts.length && <p style={{ font: font(400, 13), color: C.grey }}>No activities yet.</p>}
      </Card>

      {showSocials && p.socials.length > 0 && (
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Eyebrow>Social</Eyebrow>
          {p.socials.map((s) => (
            <button key={s.platform} type="button" onClick={() => act.toast(`Opens ${s.platform} · ${s.handle} (demo handle, link disabled)`)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 50, padding: '0 16px', borderRadius: R.pill, border: `1px solid ${C.subtle}`, background: C.white, color: C.ink, font: font(600, 14) }}>
              <span>{s.platform}</span><span style={{ font: font(500, 12, 1, 'mono'), color: C.grey }}>{s.handle} ↗</span>
            </button>
          ))}
        </Card>
      )}

      {isMe && isApprover(getSession()) && (
        <Card style={{ padding: '18px 18px 8px' }}>
          <CardTitle right={<Tag label="M+E" bg={C.soft} fg={C.violet} />}>Proposals to review</CardTitle>
          {Object.values(data.activities).filter((a) => a.review === 'pending').map((a) => (
            <button key={a.id} type="button" onClick={() => navigate({ name: 'activity', id: a.id })}
              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 0', border: 0, borderBottom: `1px solid ${C.divider}`, background: 'transparent', textAlign: 'left', minHeight: 52 }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ font: font(600, 14, 1.2), color: C.ink }}>{a.name}</span>
                <span style={{ font: font(400, 11), color: C.grey }}>{`${data.ambassadors[a.proposedBy ?? a.organiserId]?.name ?? 'Member'} · ${dayLabel(ymdIn(a.startsAt, a.timeZone))}`}</span>
              </span>
              <span style={{ font: font(600, 13), color: C.violet }}>{a.proposedBy === data.me ? 'Yours' : 'Review ›'}</span>
            </button>
          ))}
          {!Object.values(data.activities).some((a) => a.review === 'pending') && <p style={{ font: font(400, 13), color: C.grey }}>Nothing waiting for review.</p>}
        </Card>
      )}

      {isMe && (
        <>
          <Card style={{ padding: '4px 16px' }}>
            <SettingsRow icon={<ImageIcon />} label="Photos" section="photos" />
            <SettingsRow icon={<PaletteIcon />} label="Appearance" section="appearance" />
            <SettingsRow icon={<LockIcon />} label="Privacy" value={pr.publicProfile ? 'Public' : 'Teams only'} section="privacy" />
            <SettingsRow icon={<LinkIcon />} label="UNIT ID & Referral" value={p.unitId ?? 'Not added'} section="referral" last />
          </Card>
          <div style={{ margin: '18px 12px 0', display: 'flex', justifyContent: 'center', gap: 8 }}>
            <Btn variant="outline" onClick={signOut}>Sign out</Btn>
            {isDemo && <Btn variant="outline" onClick={() => act.resetDemo()}>Reset demo data</Btn>}
          </div>
        </>
      )}
    </div>
  );
}

/** One line in the Me settings list: opens Edit profile at that section. */
function SettingsRow({ icon, label, value, section, last }: { icon: ReactNode; label: string; value?: string; section: EditSection; last?: boolean }) {
  const { act } = useApp();
  return (
    <button type="button" className="press" onClick={() => act.openSheet({ kind: 'profile', section })}
      style={{ width: '100%', minHeight: 56, display: 'grid', gridTemplateColumns: '28px 1fr auto 20px', gap: 12, alignItems: 'center', padding: '6px 0', border: 0, borderBottom: last ? 0 : `1px solid ${C.divider}`, background: 'transparent', color: C.ink, textAlign: 'left' }}>
      <span style={{ display: 'inline-flex', color: C.ink }}>{icon}</span>
      <span style={{ font: font(600, 15) }}>{label}</span>
      <span style={{ font: font(500, 12), color: C.grey, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value ?? ''}</span>
      <span style={{ display: 'inline-flex', color: C.grey }}><ChevronIcon size={18} /></span>
    </button>
  );
}

/**
 * UNIT affiliations: one compact row (icon, up to three badges, +N), expandable for the list.
 * Others see what the server sent: the full list with the full profile, else the primary only.
 */
function AffiliationsRow({ person: p, isMe }: { person: Ambassador; isMe: boolean }) {
  const { state: { data }, act } = useApp();
  const [open, setOpen] = useState(false);
  const listId = useId();
  if (!p.affiliations.length && !isMe) return null;
  const catalogue = orderedSeries(data.series);
  const defs = p.affiliations.map((id) => catalogue.find((s) => s.id === id) ?? { id, label: seriesLabel(data.series, id), referenceName: id, order: 999, status: 'upcoming' as const });
  const extra = defs.length - 3;
  if (!defs.length) {
    return (
      <Card style={{ padding: '4px 16px' }}>
        <button type="button" className="press" onClick={() => act.openSheet({ kind: 'profile', section: 'units' })}
          style={{ width: '100%', minHeight: 56, display: 'grid', gridTemplateColumns: '28px 1fr 20px', gap: 12, alignItems: 'center', border: 0, background: 'transparent', color: C.ink, textAlign: 'left', padding: 0 }}>
          <span style={{ display: 'inline-flex', color: C.violet }}><UnitIcon /></span>
          <span style={{ font: font(600, 15) }}>Add your UNITs</span>
          <span style={{ display: 'inline-flex', color: C.grey }}><ChevronIcon size={18} /></span>
        </button>
      </Card>
    );
  }
  return (
    <Card style={{ padding: '4px 16px' }}>
      <button type="button" className="press" aria-expanded={open} aria-controls={listId} onClick={() => setOpen((x) => !x)}
        aria-label={`UNITs: ${defs.map((d) => d.label).join(', ')}`}
        style={{ width: '100%', minHeight: 56, display: 'grid', gridTemplateColumns: '28px auto 1fr 20px', gap: 12, alignItems: 'center', border: 0, background: 'transparent', color: C.ink, textAlign: 'left', padding: 0 }}>
        <span style={{ display: 'inline-flex', color: C.violet }}><UnitIcon /></span>
        <span style={{ font: font(600, 15) }}>UNITs</span>
        <span aria-hidden="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 0 }}>
          {defs.slice(0, 3).map((d, i) => <span key={d.id} style={{ marginLeft: i ? -6 : 0 }}><UnitBadge series={d} size={30} /></span>)}
          {extra > 0 && <span style={{ marginLeft: 4, height: 26, padding: '0 8px', borderRadius: R.pill, background: C.paper, border: `1px solid ${C.subtle}`, font: font(600, 12, 1), color: C.grey, display: 'inline-flex', alignItems: 'center' }}>{`+${extra}`}</span>}
        </span>
        <span style={{ display: 'inline-flex', color: C.grey, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}><ChevronIcon size={18} /></span>
      </button>
      {open && (
        <div id={listId} style={{ display: 'flex', flexDirection: 'column', paddingBottom: 6 }}>
          {defs.map((d, i) => (
            <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '30px 1fr auto', gap: 12, alignItems: 'center', minHeight: 44, borderTop: `1px solid ${C.divider}` }}>
              <UnitBadge series={d} size={30} />
              <span style={{ font: font(600, 14) }}>{d.label}</span>
              {i === 0 && defs.length > 1 ? <Tag label="PRIMARY" bg={C.soft} fg={C.violet} /> : <span />}
            </div>
          ))}
          {isMe && (
            <button type="button" onClick={() => act.openSheet({ kind: 'profile', section: 'units' })}
              style={{ alignSelf: 'flex-start', minHeight: 44, border: 0, padding: 0, background: 'transparent', color: C.violet, font: font(600, 13), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <PencilIcon size={16} />Edit UNITs
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * Another member's UNIT ID & referral (member-supplied). The API sends the values only when the
 * owner turned sharing on and the profile is visible to the viewer, so the card appears only
 * when something was sent. Copy and Share are user-initiated; the share contains the link only,
 * never the UNIT ID. Values render as text, never as HTML. The owner edits theirs in Edit profile.
 */
function UnitReferralCard({ person: p }: { person: Ambassador }) {
  const { act } = useApp();
  if (!p.unitId && !p.referralUrl) return null;
  const copy = async () => {
    if (!p.referralUrl) return;
    act.toast((await copyText(p.referralUrl)) ? 'Referral link copied' : 'Could not copy. Press and hold the link to copy it.');
  };
  const share = async () => {
    if (!p.referralUrl) return;
    const r = await nativeShare({ title: 'UNIT referral link', text: 'Join me on UNIT', url: p.referralUrl });
    if (r === 'unsupported' || r === 'failed') await copy();
  };
  const row = (label: string, value: ReactNode) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ font: font(500, 11), color: C.grey }}>{label}</span>
      {value}
    </div>
  );
  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Eyebrow>UNIT ID &amp; referral</Eyebrow>
      {p.unitId && row('UNIT ID', <span style={{ font: font(600, 15, 1.3), color: C.ink, overflowWrap: 'anywhere' }}>{p.unitId}</span>)}
      {p.referralUrl && row('Referral link', <span title={p.referralUrl} style={{ font: font(500, 13, 1.35, 'mono'), color: C.violet, overflowWrap: 'anywhere' }}>{displayLink(p.referralUrl)}</span>)}
      {p.referralUrl && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn size="md" variant="outline" onClick={() => void copy()}>Copy link</Btn>
          <Btn size="md" variant="outline" onClick={() => void share()}>Share</Btn>
        </div>
      )}
    </Card>
  );
}
